import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Radio, Wifi, Phone, ArrowRightLeft, Power } from 'lucide-react';
import { coax } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';

function fmtDate(s: string | null): string {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const diffMin = Math.round((Date.now() - d.getTime()) / 60000);
  if (diffMin < 1) return 'à l\'instant';
  if (diffMin < 60) return `il y a ${diffMin} min`;
  const h = Math.round(diffMin / 60);
  if (h < 24) return `il y a ${h} h`;
  return `il y a ${Math.round(h / 24)} j`;
}

export default function CoaxEndpoints() {
  const navigate = useNavigate();
  const { data: list, isLoading } = useQuery({
    queryKey: ['coax', 'endpoints'],
    queryFn: () => coax.endpoints(),
  });

  const online = list?.filter((e) => e.last_link_status === 'on').length ?? 0;
  const total  = list?.length ?? 0;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <PageHeader
        title="Coax Endpoints"
        subtitle={total ? `${online}/${total} en ligne` : '…'}
      />

      <div className="flex-1 overflow-auto p-4">
        {isLoading ? (
          <div className="text-text-faint text-center py-12 italic">Chargement…</div>
        ) : !list?.length ? (
          <div className="card card-pad text-center py-12">
            <Radio size={32} className="mx-auto text-text-faint mb-3" />
            <div className="text-sm font-semibold">Aucun Endpoint coax</div>
            <div className="text-xs text-text-muted mt-1">Discover ses Masters d'abord pour ingester les EPs.</div>
          </div>
        ) : (
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-text-muted bg-surface-hi">
                <tr>
                  <th className="px-3 py-2 font-medium w-2"></th>
                  <th className="px-3 py-2 font-medium">Note / Client</th>
                  <th className="px-3 py-2 font-medium">MAC</th>
                  <th className="px-3 py-2 font-medium">Modèle</th>
                  <th className="px-3 py-2 font-medium">Mode</th>
                  <th className="px-3 py-2 font-medium">Master</th>
                  <th className="px-3 py-2 font-medium">Service</th>
                  <th className="px-3 py-2 font-medium">Diag</th>
                  <th className="px-3 py-2 font-medium">Dernière mesure</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {list.map((e) => (
                  <tr
                    key={e.id}
                    className="hover:bg-surface-hi/40 cursor-pointer"
                    onClick={() => navigate(`/app/coax/endpoints/${e.id}`)}
                  >
                    <td className="px-3 py-2">
                      <Power size={12} className={
                        e.last_link_status === 'on'
                          ? 'text-emerald-400'
                          : 'text-text-faint'
                      } />
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{e.note ?? <span className="text-text-faint italic">—</span>}</div>
                      {e.customer ? (
                        <div className="text-xs text-text-muted">{e.customer.name}</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{e.mac}</td>
                    <td className="px-3 py-2 text-text-muted">{e.model ?? '—'}</td>
                    <td className="px-3 py-2">
                      <span className={
                        e.mode === 'routed'
                          ? 'inline-flex items-center gap-1 text-sky-400 text-xs'
                          : 'inline-flex items-center gap-1 text-text-muted text-xs'
                      }>
                        <ArrowRightLeft size={11} />{e.mode}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {e.master ? (
                        <span title={e.master.management_address}>{e.master.name ?? e.master.management_address}</span>
                      ) : <span className="text-text-faint">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex gap-2 text-xs">
                        {e.wifi_mac ? <Wifi size={12} className="text-text-muted" /> : null}
                        {e.voice_enabled ? <Phone size={12} className="text-text-muted" /> : null}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs font-mono">
                      {e.last_up_rate_mbps != null ? (
                        <span>
                          <span className="text-text">↑{e.last_up_rate_mbps}</span>
                          <span className="text-text-faint"> / </span>
                          <span className="text-text">↓{e.last_dn_rate_mbps}</span>
                          <span className="text-text-muted text-[10px]"> Mbps</span>
                          {e.last_up_atten_db != null ? (
                            <span className="text-text-muted block text-[10px]">{e.last_up_atten_db}/{e.last_dn_atten_db} dB</span>
                          ) : null}
                        </span>
                      ) : <span className="text-text-faint">pas mesuré</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-text-muted">{fmtDate(e.last_diag_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
